import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { EVENT_TYPES, STATUS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/TimeInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { TownPicker } from "@/components/TownPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
  </div>
);

export const EventInfoFields = ({ value, onChange, withStatus }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target !== undefined && e?.target?.value !== undefined ? e.target.value : e });
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2"><Field label="Población"><TownPicker value={value.town} onChange={set("town")} /></Field></div>
      <div className="col-span-2"><Field label="Nombre del evento"><Input value={value.name} onChange={set("name")} data-testid="event-name-input" /></Field></div>
      <Field label="Tipo">
        <Select value={value.type} onValueChange={set("type")}>
          <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(EVENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Fecha"><Input type="date" value={value.date} onChange={set("date")} data-testid="event-date-input" /></Field>
      <Field label="Hora inicio"><TimeInput value={value.start_time} onChange={set("start_time")} data-testid="event-start-input" /></Field>
      <Field label="Hora fin"><TimeInput value={value.end_time} onChange={set("end_time")} data-testid="event-end-input" /></Field>
      {withStatus && (
        <Field label="Estado">
          <Select value={value.status} onValueChange={set("status")}>
            <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <div className={withStatus ? "" : "col-span-2"}><Field label="Lugar / zona"><Input value={value.location} onChange={set("location")} data-testid="event-location-input" /></Field></div>
      <div className="col-span-2"><Field label="Descripción"><Textarea rows={2} value={value.description} onChange={set("description")} data-testid="event-description-input" /></Field></div>
    </div>
  );
};

const blank = () => ({ name: "", type: "carrera", date: new Date().toISOString().slice(0, 10), start_time: "10:00", end_time: "13:00", location: "", description: "", status: "planificado", town: null });

export const NewEventDialog = ({ open, onOpenChange, onCreated }) => {
  const [v, setV] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setV(blank()); }, [open]);
  const submit = async () => {
    if (!v.town?.name) return toast.error("Elija la población del evento");
    if (!v.name.trim()) return toast.error("Indique el nombre del evento");
    setBusy(true);
    try {
      const r = await api.post("/events", { ...v, resources: { policia_pie: 4, patrullas_coche: 1, patrullas_moto: 1, auxiliares: 2, proteccion_civil: 4, organizadores: 2 } });
      toast.success("Evento creado");
      onOpenChange(false);
      onCreated(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-lg" data-testid="new-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Nuevo evento</DialogTitle>
          <DialogDescription>Elija primero la población: el mapa se abrirá centrado en ella.</DialogDescription>
        </DialogHeader>
        <EventInfoFields value={v} onChange={setV} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="new-event-submit-button">Crear y abrir mapa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DuplicateDialog = ({ source, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  useEffect(() => {
    if (!source) return;
    const y = parseInt(source.date.slice(0, 4), 10) + 1;
    const yr = /\b(19|20)\d{2}\b/;
    setName(yr.test(source.name) ? source.name.replace(yr, String(y)) : `${source.name} ${y}`);
    setDate(`${y}${source.date.slice(4)}`);
  }, [source]);
  const submit = async () => {
    try {
      const r = await api.post(`/events/${source.id}/duplicate`, { name, date });
      toast.success("Evento duplicado con todos sus cortes y zonas");
      onClose();
      onDone(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!source} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200" data-testid="duplicate-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Duplicar evento</DialogTitle>
          <DialogDescription>Se copian recorrido, cortes, zonas y recursos. El plan se recalcula en el nuevo evento.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="duplicate-name-input" /></Field>
          <Field label="Nueva fecha"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="duplicate-date-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} data-testid="duplicate-submit-button">Duplicar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
